/* eslint-disable max-lines */
/* eslint-disable camelcase */
import { FiltersTreeContext } from 'contexts/ExploreFiltersTreeContext';
import React, { useContext, useEffect, useState } from 'react';
import { getFiltersCounts } from 'selectors/envelopes';
import { useSelector } from 'store';
import { Tag, TagGroup } from 'types';

type ResourceFilterProps = {
  tagGroup: TagGroup & { count?: number };
  tags: (Tag & { count?: number })[];
  searchValue: string;
  handleInputChange: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    exclude: boolean,
    state: string,
    checked: string | undefined,
    isTag: boolean
  ) => void;
  treeContext: React.Context<FiltersTreeContext>;
};

const TagGroupsFilterItem: React.FC<ResourceFilterProps> = (props) => {
  const { tagGroup, tags, handleInputChange, treeContext, searchValue } = props;

  const { treeParams } = useContext(treeContext);

  const tagGroupsKey = 'tag_value_groups';
  const tagsKey = 'tags';

  const filterCounts = useSelector(getFiltersCounts).tags;

  const [isOpen, setIsOpen] = useState(false);

  const filterTagGroupsItems = (treeParams[tagGroupsKey] ?? []) as string[];
  const notFilterTagGroupsItems = (treeParams[`not_${tagGroupsKey}`] ?? []) as string[];

  const filterTagsItems = (treeParams[tagsKey] ?? []) as string[];
  const notFilterTagsItems = (treeParams[`not_${tagsKey}`] ?? []) as string[];

  const allTagsFilterItems = [
    ...filterTagsItems,
    ...filterTagGroupsItems,
    ...notFilterTagsItems,
    ...notFilterTagGroupsItems,
  ];

  const selectedTags = tags.filter((t) => allTagsFilterItems.includes(t.uuid));

  useEffect(() => {
    if (searchValue) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [searchValue]);

  const handleTagGroupChange = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    const { shiftKey: exclude } = event.nativeEvent;
    const { id: state } = event.currentTarget;

    const checked = event.currentTarget.dataset.checked === 'true' ? 'false' : 'true';

    handleInputChange(event, exclude, state, checked, false);

    tags
      .filter((r) => allTagsFilterItems.includes(r.uuid))
      .forEach((r) => {
        handleInputChange(
          event,
          notFilterTagGroupsItems.includes(tagGroup.uuid),
          r.uuid,
          'false',
          true
        );
      });
  };

  const handleTagChange = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    const { shiftKey: exclude } = event.nativeEvent;
    const { id: state } = event.currentTarget;

    if (allTagsFilterItems.includes(state) || allTagsFilterItems.includes(tagGroup.uuid)) {
      handleInputChange(event, exclude, state, 'false', true);
    } else {
      handleInputChange(event, exclude, state, 'true', true);
    }

    if (allTagsFilterItems.includes(tagGroup.uuid)) {
      handleInputChange(event, exclude, tagGroup.uuid, 'false', false);
      tags
        .filter((t) => t.uuid !== state && !allTagsFilterItems.includes(t.uuid))
        .forEach((t) => {
          handleInputChange(
            event,
            notFilterTagGroupsItems.includes(tagGroup.uuid),
            t.uuid,
            'true',
            true
          );
        });
    }
  };

  return (
    <>
      <div
        id={tagGroup.uuid}
        role="button"
        aria-hidden
        className="w-full py-1 px-2 relative flex flex-row gap-1 items-start cursor-pointer"
        data-checked={allTagsFilterItems.includes(tagGroup.uuid)}
        onClick={(e): void => handleTagGroupChange(e)}
      >
        <div className="w-full relative flex flex-col items-start justify-center text-small cursor-pointer">
          <div className="w-full relative flex flex-row gap-1 justify-start items-start">
            <input
              id={tagGroup.uuid}
              data-testid={`${tagGroup.uuid}-checkbox`}
              type="checkbox"
              className={`form-checkbox litlingo-checkbox h-4 w-4 transition duration-150 ease-in-out cursor-pointer 
              ${
                tags.length > 0 && selectedTags.length > 0 && selectedTags.length < tags.length
                  ? 'checkbox-select-all-partial'
                  : ''
              }
              ${
                notFilterTagGroupsItems.includes(tagGroup.uuid)
                  ? 'litlingo-checkbox-negated'
                  : 'litlingo-checkbox'
              }`}
              checked={allTagsFilterItems.includes(tagGroup.uuid)}
            />

            <label
              htmlFor={tagGroup.uuid}
              className="w-full flex flex-row justify-between gap-1 text-small cursor-pointer"
            >
              <span
                className="w-full select-none font-semibold"
                style={{ wordBreak: 'break-word' }}
              >
                {tagGroup.name}
              </span>
              <span className="text-litlingo-primary">
                {!tagGroup.count && tagGroup.count !== 0 ? '-' : tagGroup.count}
              </span>
            </label>
            {tags.length > 0 && (
              <button
                type="button"
                onClick={(e): void => {
                  e.stopPropagation();
                  setIsOpen(!isOpen);
                }}
                className="pt-1 focus:outline-none"
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`${isOpen ? 'transform rotate-90' : ''}`}
                >
                  <path
                    d="M2.08961 9.91604C2.1425 9.96937 2.21437 9.99955 2.28948 9.99999C2.32675 10.0003 2.3637 9.99307 2.39807 9.97864C2.43244 9.9642 2.4635 9.9429 2.48934 9.91604L7.20219 5.20319C7.22866 5.17707 7.24968 5.14595 7.26402 5.11164C7.27837 5.07733 7.28575 5.04051 7.28575 5.00333C7.28575 4.96614 7.27837 4.92932 7.26402 4.89501C7.24968 4.8607 7.22866 4.82958 7.20219 4.80346L2.48934 0.0896109C2.46343 0.0619091 2.43221 0.0397035 2.39754 0.0243155C2.36287 0.00892748 2.32545 0.000671647 2.28753 3.93014e-05C2.2496 -0.000593044 2.21193 0.00641102 2.17677 0.0206347C2.1416 0.0348583 2.10966 0.0560109 2.08283 0.0828334C2.05601 0.109656 2.03486 0.1416 2.02063 0.176766C2.00641 0.211931 1.99941 0.249598 2.00004 0.287526C2.00067 0.325453 2.00893 0.362866 2.02432 0.397537C2.0397 0.432209 2.06191 0.46343 2.08961 0.489344L6.6026 5.00333L2.08961 9.51631C2.06326 9.5425 2.04235 9.57364 2.02809 9.60794C2.01382 9.64224 2.00647 9.67903 2.00647 9.71618C2.00647 9.75333 2.01382 9.79011 2.02809 9.82441C2.04235 9.85871 2.06326 9.88985 2.08961 9.91604Z"
                    fill="#333333"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
      <div>
        {isOpen &&
          tags
            .filter((t) => t.value.toLowerCase().includes(searchValue.toLowerCase()))
            .map((tag) => {
              const tagNegated =
                notFilterTagsItems.includes(tag.uuid) ||
                notFilterTagGroupsItems.includes(tagGroup.uuid);
              // @ts-ignore
              const count = filterCounts?.find((c) => c.value === tag.value)?.envelopes_count;

              return (
                <div
                  id={tag.uuid}
                  key={tag.uuid}
                  role="button"
                  aria-hidden
                  className="w-full py-1 px-2 bg-white relative flex flex-row items-start cursor-pointer"
                  data-checked={allTagsFilterItems.includes(tag.uuid)}
                  onClick={(e): void => handleTagChange(e)}
                >
                  <input
                    id={tag.uuid}
                    data-testid={`${tag.uuid}-checkbox`}
                    type="checkbox"
                    className={`form-checkbox litlingo-checkbox mr-1 h-4 w-4 transition duration-150 ease-in-out cursor-pointer ${
                      tagNegated ? 'litlingo-checkbox-negated' : 'litlingo-checkbox'
                    }`}
                    checked={
                      allTagsFilterItems.includes(tag.uuid) ||
                      allTagsFilterItems.includes(tagGroup.uuid)
                    }
                  />

                  <label
                    htmlFor={tag.uuid}
                    className="w-full flex flex-row justify-between gap-1 text-small cursor-pointer"
                  >
                    <span className="w-full select-none" style={{ wordBreak: 'break-word' }}>
                      {tag.value}
                    </span>
                    <span className="text-litlingo-primary">
                      {!count && count !== 0 ? '-' : count}
                    </span>
                  </label>
                </div>
              );
            })}
      </div>
    </>
  );
};

export default TagGroupsFilterItem;
